/**
 * Created by jonas on 2020-10-03.
 */
import {MatrixUtils} from "./MatrixUtils";


export interface StateMatrixCoordinate {
    column:number;
    row:number;
}

export class StateMatrix<T> {
    get matrix():T[][] {
        return this._matrix;
    }

    set matrix(value:T[][]) {
        this._matrix = value;
    }

    private _matrix:T[][];
    public readonly columns:number;
    public readonly rows:number;

    public static tuple2Coord(tuple:[number, number]) {
        return {column : tuple[0], row : tuple[1]};
    }

    constructor(initMatrix?:T[][]) {
        this._matrix = initMatrix ? MatrixUtils.cloneMatrix(initMatrix) : [];
    }

    public initWithValue(columns:number, rows:number, initValue:T) {
        this._matrix = [];
        for(let i:number = 0; i < columns; i++) {
            this._matrix.push([]);
            for(let j:number = 0; j < rows; j++) {
                this._matrix[i].push(initValue);
            }
        }
    }

    /**
     * Initializes matrix with a value for each cell with the dimensions matched to other matrix
     * @param initValue Init value for each cell
     * @param dimensionsMatrix Dimentions from this matrix is used as a template
     */
    public initWithValueAndMatchDimensionsFromMatrix(initValue:T, dimensionsMatrix:any[][]) {
        this._matrix = [];
        for(let i:number = 0; i < dimensionsMatrix.length; i++) {
            this._matrix.push([]);
            for(let j:number = 0; j < dimensionsMatrix[i].length; j++) {
                this._matrix[i].push(initValue);
            }
        }
    }


    public getAllCoordinatesForValue(value:T):StateMatrixCoordinate[] {
        const result:StateMatrixCoordinate[] = []
        for(let i:number = 0; i < this._matrix.length; i++) {
            for(let j:number = 0; j < this._matrix[i].length; j++) {
                if(this._matrix[i][j] === value) {
                    result.push({column : i, row : j});
                }

            }
        }
        return result;
    }

    public getAllCoordinatesForValues(values:T[]):StateMatrixCoordinate[] {
        const result:StateMatrixCoordinate[] = []

        for(let value of values) {
            result.push(...this.getAllCoordinatesForValue(value));
        }

        return result;
    }


    /**
     *
     * @param callback (value:T,column?:number,row?:number)=>boolean. Function to execute on each element.
     */
    public getAllCoordinatesWhere(callback:(value:T, column:number, row:number) => boolean):StateMatrixCoordinate[] {
        const result:StateMatrixCoordinate[] = []
        for(let i:number = 0; i < this._matrix.length; i++) {
            for(let j:number = 0; j < this._matrix[i].length; j++) {
                if(callback(this._matrix[i][j], i, j)) {
                    result.push({column : i, row : j});
                }

            }
        }
        return result;
    }


    /**
     * Gets all coordinates for value in a column
     * @param column The column to search
     * @param value The search value
     */
    public getCoordinatesForValueInColumn(column:number, value:T):StateMatrixCoordinate[] {
        const result = [];
        for(let j:number = 0; j < this._matrix[column].length; j++) {
            if(this._matrix[column][j] === value) {
                result.push({column : column, row : j});
            }
        }
        return result;
    }

    /**
     * Sets value for each coordiante.
     * @param coordinates
     * @param value
     */
    public setValueForCoordinates(coordinates:StateMatrixCoordinate[], value:T) {
        for(let coord of coordinates) {
            this.matrix[coord.column][coord.row] = value;
        }
    }

    /**
     * Sets all cells in column to a value
     * @param column The column to be changed
     * @param value The value to be set
     */
    public setValueForColumn(column:number, value:T) {
        for(let i:number = 0; i < this.matrix[column].length; i++) {
            this.matrix[column][i] = value;
        }
    }


    /**
     * Converst matrix to string to be easily printed in console
     */
    public toString():string {
        let str = "";

        for(let i:number = 0; i < this._matrix.length; i++) {
            let row:string = `${i}:(${this._matrix[i].length}) [`;
            for(let j:number = 0; j < this._matrix[i].length; j++) {
                row += "" + this._matrix[i][j];

                if(j != this._matrix[i].length - 1) {
                    row += ","
                }
            }

            row += "]";
            str += row + "\n";
        }
        return str;
    }


    /**
     * Searches all columns for value. Returns array with a bool for each searched column.
     * @param value The value to search for
     */
    public isValueInColumns(value:T):boolean[] {
        const result:boolean[] = [];
        for(let i:number = 0; i < this._matrix.length; i++) {
            result.push(this.isValueInColumn(i, value));
        }
        return result;
    }


    /**
     * Searched for a value in a column. Returns whether it's found or not (bool)
     * @param column The column to search in
     * @param value The value to search for
     */
    public isValueInColumn(column:number, value:T):boolean {
        for(let i:number = 0; i < this._matrix[column].length; i++) {
            if(this._matrix[column][i] === value) {
                return true;
            }
        }

        return false;
    }
}